import type { CapabilityRegistry } from '@trimble/web3d';
import type { ControlMenuItemType } from '@trimble/ui-components/solidjs/control-menu/types';
import { t } from '@trimble/i18n';
import { MarkupCapability } from '@trimble/web3d-capabilities';
import { MarkupsCapabilityProvider } from '@trimble/web3d-providers';
import { Accessor, batch, createMemo, createSignal } from 'solid-js';

export interface UseToolbarMarkupStateResult {
  readonly markupMenuItems: Accessor<ControlMenuItemType[]>;
  readonly isMarkupActive: Accessor<boolean>;
  readonly activeMarkupIcon: Accessor<string>;
  deactivateMarkup(): void;
}

/**
 * @intent Owns markup toolbar dropdown state for annotation markup tools.
 */
export const useToolbarMarkupState = (
  capabilityRegistry: CapabilityRegistry
): UseToolbarMarkupStateResult => {
  const markupCapability = capabilityRegistry.get(MarkupCapability);
  const markupsProvider = capabilityRegistry.get(MarkupsCapabilityProvider);
  const [selectedMarkupItemId, setSelectedMarkupItemId] = createSignal<
    string | undefined
  >();
  const [lastMarkupItemId, setLastMarkupItemId] = createSignal<
    string | undefined
  >();

  const isMarkupActive = createMemo(
    (): boolean => markupCapability()?.isActive() ?? false
  );

  const activeMarkupItemId = createMemo((): string | undefined =>
    isMarkupActive() ? selectedMarkupItemId() : undefined
  );

  const deactivateMarkup = (): void =>
    batch(() => {
      markupCapability()?.deactivate();
      setSelectedMarkupItemId(undefined);
    });

  const startMarkupTool = (itemId: string, start: () => void): void =>
    batch(() => {
      if (activeMarkupItemId() === itemId) {
        deactivateMarkup();
        return;
      }
      setSelectedMarkupItemId(itemId);
      setLastMarkupItemId(itemId);
      start();
    });

  const markupMenuItems = createMemo((): ControlMenuItemType[] => [
    {
      id: 'cloud-markup',
      type: 'action' as const,
      label: t('web3D.toolbar.actions.cloudMarkup'),
      icon: 'tc-icon-markup-cloud',
      selected: activeMarkupItemId() === 'cloud-markup',
      onClick: () =>
        startMarkupTool('cloud-markup', () =>
          markupCapability()?.startCloudMarkupCreation()
        ),
    },
    {
      id: 'arrow-markup',
      type: 'action' as const,
      label: t('web3D.toolbar.actions.arrowMarkup'),
      icon: 'tc-icon-markup-arrow',
      selected: activeMarkupItemId() === 'arrow-markup',
      onClick: () =>
        startMarkupTool('arrow-markup', () =>
          markupCapability()?.startArrowMarkupCreation()
        ),
    },
    {
      id: 'line-markup',
      type: 'action' as const,
      label: t('web3D.toolbar.actions.lineMarkup'),
      icon: 'tc-icon-markup-line',
      selected: activeMarkupItemId() === 'line-markup',
      onClick: () =>
        startMarkupTool('line-markup', () =>
          markupCapability()?.startLineMarkupCreation()
        ),
    },
    {
      id: 'text-markup',
      type: 'action' as const,
      label: t('web3D.toolbar.actions.textMarkup'),
      icon: 'tc-icon-markup-text-active',
      selected: activeMarkupItemId() === 'text-markup',
      onClick: () =>
        startMarkupTool('text-markup', () =>
          markupCapability()?.startTextMarkupCreation()
        ),
    },
    {
      id: 'draw-markup',
      type: 'action' as const,
      label: t('web3D.toolbar.actions.drawMarkup'),
      icon: 'tc-icon-gesture',
      selected: activeMarkupItemId() === 'draw-markup',
      onClick: () =>
        startMarkupTool('draw-markup', () =>
          markupCapability()?.startDrawMarkupCreation()
        ),
    },
    {
      id: 'markups-divider',
      type: 'divider' as const,
    },
    {
      id: 'delete-markups',
      type: 'action' as const,
      label: t('web3D.toolbar.actions.deleteAllMarkups'),
      icon: 'tc-icon-delete',
      replayable: false,
      onClick: () => markupsProvider()?.removeAllMarkups(),
    },
  ]);

  const activeMarkupIcon = createMemo((): string => {
    const itemId = lastMarkupItemId();
    if (!itemId) {
      return 'tc-icon-markup-cloud';
    }
    const item = markupMenuItems().find((menuItem) => menuItem.id === itemId);
    return item?.type === 'action' && typeof item.icon === 'string'
      ? item.icon
      : 'tc-icon-markup-cloud';
  });

  return {
    markupMenuItems,
    isMarkupActive,
    activeMarkupIcon,
    deactivateMarkup,
  };
};
